'use client';

import Link from 'next/link';
import { cn } from '@/lib/utils';
import { ArrowRight } from 'lucide-react';
import { LANDING_CONTENT } from '../constants/content';
import { useLocale } from '@/features/marketing/hooks/useLocale';
import HeroConvergenceFigure from './HeroConvergenceFigure';
import { LANDING_PRIMARY_CTA_RECIPE } from '../ui/cta-recipe';
import { PAPER_BAND, PAPER_INK, PAPER_RULE, PAPER_FOCUS, PAPER_VARIANT } from '../ui/paper-tokens';

/**
 * HeroSection — split-product hero (2026-07-10 landing round-2 rebuild, Lane 1
 * "Firul viu"). Rebuilt per r2-judge-A-verdict.md + r2-debate-motion-ruling.md
 * (masterplan §3 is the spec of record):
 *   1. Static SSR two-line H1 (`hero.outcome.h1Line1`/`h1Line2` — Alex's final,
 *      polish-locked copy, masterplan §0) replaces the old single-line `h1`
 *      key (kept populated elsewhere only for compile-compat until this lane
 *      lands — this IS that landing).
 *   2. The right-hand product visual is now HeroJourneyFigure (the "Firul viu"
 *      lead-journey figure), replacing LeadJourneyVignette.tsx (DELETED this
 *      change — its ghost cards, aria-hidden root, and phase-starts-complete
 *      SSR defect were named kill conditions in Judge A's autopsy).
 * Kill list applied here (masterplan §2/§3): the old per-column glow ellipse +
 * dot grid + mobile-only glow were replaced by ONE full-section `.hero-band`
 * amber wash (globals.css) at R2 — Judge A traced the old "amber square"
 * artifact to that glow's clipping-child geometry; a full-band wash couldn't
 * clip the same way. R7 hallmark paper flip (2026-07-17): the `hero-band`
 * class itself is now DROPPED from this section entirely (editorial genre's
 * own kill list bans a page-chrome amber glow/gradient outright) — see the
 * inline comment on the section's own className below.
 *
 * R8 restructure (2026-08-12, §H, `.claude/handoffs/2026-08-12-r8-restructure-
 * contract.md`): the right-hand visual is now `HeroConvergenceFigure` — a
 * light-paper "fragmented sources -> CENTRALIZED -> Cosmos/CMG" exhibit,
 * replacing `HeroJourneyFigure` (DELETED this change, along with its whole
 * canvas/controller/geometry/content set — sole consumer was this file).
 * The left column (H1/sub/CTAs/microcopy, R2 build above) is UNCHANGED; only
 * the right column's figure and this file's own import swap.
 */
function HeroSectionInner() {
  const { hero } = LANDING_CONTENT;
  const { t } = useLocale();
  const { outcome } = hero;

  return (
    <section
      // 2026-07-10 live-verify fix (LV3, Judge A's named "vulnerable viewport"):
      // at 1366x768 the CTAs sat below the fold. lg:min-h-[800px] alone already
      // exceeds a 768px-tall viewport, and the row height is dominated by the
      // fixed 720px HeroJourneyFigure (DESKTOP_MAX_HEIGHT), not by this column's
      // own content — so the effective lever is the SECTION's own vertical
      // padding/floor at the in-between xl step (1280-1535px), not the figure.
      // xl: shrinks padding + drops the artificial min-height floor so the
      // section sizes to its actual (figure-driven) content height instead of
      // forcing extra centered slack; 2xl: restores the original desktop-1920+
      // values so that already-verified viewport is unchanged.
      //
      // R6 judge §2.6 padding axis inversion (2026-07-17): the axis is now
      // BOTTOM-heavy at every breakpoint — pb:pt ratio 1.33-1.67 (hallmark
      // gate 44a bans a symmetric/top-heavy hero pad) — only the BOTTOM value
      // grew at each step; the CRITICAL xl:pt-14/xl:min-h-0 fold fix above
      // (LV3) is preserved byte-identical, only xl:pb-8 grows to xl:pb-20.
      // Lead live-measures 1280x800 + 1366x768 after integration to confirm
      // the fold clearance still holds with the taller bottom pad.
      className={cn(
        // R7 hallmark paper flip: the `hero-band` CSS hook (globals.css) painted
        // a radial amber glow + grain texture directly on this band — the design
        // contract's explicit kill list ("DELETE all radial amber glow /
        // gradient backgrounds ... painted directly on the band"). `hero-band`
        // is exclusively THIS component's own CSS hook (verified: no other live
        // page component references it), so dropping the class reference here
        // is sufficient — its globals.css rule is left untouched for the
        // gallery specimen file that also cites it.
        'relative flex items-center',
        PAPER_BAND.base.bg,
        'pt-20 pb-28 sm:pt-24 sm:pb-32 lg:pt-24 lg:pb-40 lg:min-h-[800px] xl:pt-14 xl:pb-20 xl:min-h-0 2xl:pt-24 2xl:pb-40 2xl:min-h-[800px]'
      )}
    >
      <div className="relative z-10 w-full max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(620px,7fr)] lg:items-center gap-10 xl:gap-16">
          {/* Left thesis — 5fr, left-aligned, static SSR text at full opacity on first
              frame (LCP contract). No entrance animation — judge-5 §2 "Hero H1 and CTA
              are present immediately." */}
          <div className="text-left">
            {/* 2xl step 5.25rem->4.75rem (P4 lead measure, 2026-07-15): Libre
                Caslon sets ~12% wider than the Inter this ladder was authored
                for — at 84px the RO H1 wrapped to 5 ragged lines ("totul"
                stranded); 76px restores the intended 2-3 line lockup. */}
            <h1
              className={cn(
                // R7 hallmark paper flip (design contract L1.2): "push clamp
                // beyond current size" — 2xl ONLY moves 4.75rem->5rem (+4px),
                // deliberately NOT touching the lg step. lg's 72px flows
                // through the xl (1280-1535px) tier untouched by any
                // font-size breakpoint of its own — that is the CRITICAL
                // LV3 fold fix at 1366x768, "preserved byte-identical" per
                // the R6 comment above; pushing lg risks reopening it. 2xl
                // (>=1536px) carries no known fold risk (2xl:min-h-[800px]
                // is generous) and was PREVIOUSLY verified safe up to
                // 5.25rem/84px on wrap alone before the P4 pull-back to
                // 76px for the 5-line RO wrap regression — 80px keeps a 4px
                // margin under that proven-bad point (half the previous
                // 8px margin: a real push, not a reckless one). Needs a
                // live wrap/fold re-check at both laptop folds (VERIFY
                // lane, design contract's own step) — flagged, not
                // assumed.
                'landing-serif-display max-w-[13ch] text-[2.75rem] sm:text-6xl lg:text-7xl 2xl:text-[5rem] leading-[0.98] lg:leading-[0.94] mb-5 lg:mb-6',
                PAPER_INK.primary.text
              )}
            >
              {/* Alex's final H1 (masterplan §0, polish-locked) — one clause per
                  line, period-split. Static SSR text, no animate-* class
                  (check:marketing-lcp-h1 — the H1 is the LCP candidate).
                  2026-07-10 live-verify fix (LV3): the 5.25rem size moved from
                  xl to 2xl — at 1366px (xl, not yet 2xl) the H1 now stays at
                  lg's 72px instead of jumping to 84px, which was the biggest
                  single contributor to pushing the CTAs below the 768px fold.
                  R5-P3 Lane C0b (design amendment §1 "the serif display law"):
                  family swap ONLY — `landing-serif-display` (globals.css)
                  swaps Inter → the real Libre Caslon Text webfont via
                  `var(--font-landing-serif)` and DROPS the old
                  `font-semibold`/`tracking-[-0.045em]` sans treatment (serif
                  ships weight 400, tracking normal); the px/leading ladder
                  above is UNCHANGED — this is the hero's LCP/CLS anchor and
                  the R5 brief's consistency-anchor node, not a resize. */}
              <span className="block">{t(outcome.h1Line1)}</span>
              <span className="block">{t(outcome.h1Line2)}</span>
            </h1>
            <p
              className={cn(
                'max-w-[52ch] text-lg lg:text-xl leading-[1.55] tracking-[-0.01em] font-normal',
                PAPER_INK.secondary.text
              )}
            >
              {t(outcome.sub)}
            </p>

            {/* Dual CTA pair — CRM trial primary (amber recipe), WhatsApp bot test
                secondary (border recipe). Full-width stacked on mobile, inline on desktop. */}
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                href={outcome.primaryCta.href}
                // v2 bio-link engagement pipeline (contract 3/6): the hero's
                // primary CTA, canonical event_name "cta_hero".
                data-track="cta_hero"
                className={cn(
                  'group inline-flex w-full sm:w-auto items-center justify-center gap-2',
                  'h-12 px-5 sm:px-6 rounded-xl text-[15px] font-semibold',
                  LANDING_PRIMARY_CTA_RECIPE
                )}
              >
                {/* 2026-08-13: the full RO label wrapped to 2 lines inside the
                    48px button, filling it with no breathing room. Measured at
                    true 320 AND 375 (both wrap); 500 fits. CSS-only swap — no
                    JS, no hydration branch, both strings server-rendered — and
                    `display:none` removes the inactive one from the
                    accessibility tree too, so a screen reader never hears the
                    label twice.
                    BREAKPOINT `min-[400px]`, settled by MEASUREMENT rather than
                    by arithmetic: the full labels render `lines=1` at 414 and
                    wrap at 375, so the swap belongs between those two. A later
                    pass pushed it to 500 on an estimated 11px/char advance, but
                    the live 320 fit bounds the real advance at <=9.6px/char, so
                    that estimate was too high — and 500 would have shown the
                    terser label on 390-430px phones (iPhone 14/15/Pro Max),
                    which is most of the real mobile traffic, for no benefit.
                    The arrow is `aria-hidden` decoration and hides below the
                    breakpoint too: it costs 24px (icon + gap) the text budget
                    needs at 320. */}
                <span className="min-[400px]:hidden">{t(outcome.primaryCta.labelShort)}</span>
                <span className="hidden min-[400px]:inline">{t(outcome.primaryCta.label)}</span>
                <ArrowRight
                  className="hidden h-4 w-4 transition-transform duration-[180ms] group-hover:translate-x-[3px] min-[400px]:block"
                  aria-hidden="true"
                />
              </Link>
              <Link
                href={outcome.secondaryCta.href}
                className={cn(
                  'inline-flex w-full sm:w-auto items-center justify-center gap-2',
                  'h-12 px-5 sm:px-6 rounded-xl',
                  // R7: secondary CTA becomes ink-outline-on-paper (design contract
                  // L1.2). PAPER_RULE.hairline (border) + PAPER_INK.primary (text)
                  // compose the recipe — paper-tokens.ts has no pre-baked "outline
                  // button" compound and is read-only for lanes this round; the
                  // hover states use the alpha-overlay technique (`hover:bg-black/5`,
                  // `hover:border-black/20` — no bracket hex) rather than a new
                  // registry entry, per the lane's binding rule. 2026-07-18 slop-
                  // audit revision pass FIX 4 (LOW, gate 26): added a distinct
                  // `active:` state (was hover-only — pressed and hovered read
                  // identically) — one consistent recipe with the other 2
                  // secondary/outline CTAs (PricingSection Starter, DemoSuccessCard).
                  'border',
                  PAPER_RULE.hairline.border,
                  PAPER_INK.primary.text,
                  'text-[15px] font-semibold',
                  'hover:border-black/20 hover:bg-black/5 active:bg-black/10',
                  'transition-[border-color,background-color] duration-150',
                  'focus-visible:outline-none focus-visible:ring-2',
                  PAPER_FOCUS.ring,
                  'focus-visible:ring-offset-2',
                  PAPER_VARIANT.baseRingOffset
                )}
              >
                {t(outcome.secondaryCta.label)}
              </Link>
            </div>
            {/* R7: paper-tokens has no "quaternary" step (DARK_INK's lowest,
                decorative-only tier) — muted is paper's own lowest legible
                ink and this is real helper copy (not decorative), so muted
                is the correct mapping, not a downgrade. */}
            <p className={cn('mt-3 text-xs leading-5 tracking-[0.01em]', PAPER_INK.muted.text)}>
              {t(outcome.microcopy)}
            </p>
          </div>

          {/* Right — R8 §H convergence exhibit: seven fragmented lead-source
              rows funnelling through one amber CENTRALIZED beat into the
              Cosmos/CMG terminus. Light-paper `<figure>` (its own hairline
              border + paper interior, HeroConvergenceFigure.tsx owns the
              frame) — the section itself carries no glow/wash of its own. */}
          <div className="relative mt-12 lg:mt-0" data-hero-convergence-column>
            <HeroConvergenceFigure />
          </div>
        </div>

        {/* Hero-end sentinel — zero-height marker at the bottom of the hero
            content. StickyMobileCTA observes [data-hero-end]: the sticky bar
            reveals only once this marker (and thus the hero CTA above it) has
            scrolled out of the viewport, so the bar never covers the hero CTA. */}
        <div data-hero-end aria-hidden="true" className="h-0" />
      </div>
    </section>
  );
}

export default function HeroSection() {
  return <HeroSectionInner />;
}
